import {Component, computed, inject, input, LOCALE_ID, signal} from '@angular/core';
import {appEventUrl} from '../games/public-game';
import {trackAppClick} from '../services/analytics';
import {EVENT_ROWS, FeaturedEvent} from './featured-event';

interface EventRow {
    id: string;
    url: string;
    title: string;
    weekday: string;
    /** The day of the month alone, big; the month goes under it, so no language's date runs long. */
    day: string;
    month: string;
    where: string;
    when: string;
    going: number;
    activities: number;
    image: string | null;
}

/**
 * The biggest upcoming events on Ludoya as a list you can scan like a listings page: a date block,
 * what and where, and how many people are going. Every row is a real event and opens in the app.
 */
@Component({
    selector: 'cb-event-list',
    // Rendered fresh in the browser, never hydrated: the page is baked days before it is read, and
    // patching that stale list's plural counts doubled them ("43 activities43 activities"). The
    // swap happens before the first paint after bootstrap and every row keeps its size, so nothing moves.
    host: {ngSkipHydration: 'true'},
    template: `
        <ol class="events">
            @for (row of rows(); track row.id) {
                <li>
                    <a class="event" [href]="row.url" target="_blank" rel="noopener" (click)="onOpen(row.id)">
                        <span class="date">
                            <span class="weekday">{{ row.weekday }}</span>
                            <span class="day">{{ row.day }}</span>
                            <span class="month">{{ row.month }}</span>
                        </span>
                        <span class="thumb" aria-hidden="true">
                            @if (row.image && !brokenImages().has(row.id)) {
                                <img [src]="row.image" alt="" width="96" height="96" loading="lazy" decoding="async" (error)="markBroken(row.id)"/>
                            } @else {
                                <svg viewBox="0 0 24 24" width="26" height="26"><path d="M4 7h16v12H4zM8 4v5M16 4v5M4 11h16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            }
                        </span>
                        <span class="what">
                            <span class="title">{{ row.title }}</span>
                            <span class="where">{{ row.where }}</span>
                        </span>
                        <span class="meta">
                            <span class="when">{{ row.when }}</span>
                            <span class="counts">
                                <span i18n="@@home-event-going">{{ row.going }} going</span>
                                @if (row.activities > 0) {
                                    <span aria-hidden="true"> · </span>
                                    <span i18n="@@home-event-activities">{row.activities, plural, =1 {1 activity} other {{{row.activities}} activities}}</span>
                                }
                            </span>
                        </span>
                        <svg class="go" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                            <path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </a>
                </li>
            }
        </ol>
    `,
    styles: [`
        :host {
            display: block;
        }

        .events {
            list-style: none;
            margin: 0;
            padding: 0;
            border-top: 1px solid rgba(255, 255, 255, 0.12);
        }

        .events li {
            border-bottom: 1px solid rgba(255, 255, 255, 0.12);
        }

        .event {
            display: grid;
            grid-template-columns: 92px 64px minmax(0, 1fr) minmax(0, 220px) 32px;
            gap: 24px;
            align-items: center;
            padding: 24px 12px;
            margin: 0 -12px;
            color: white;
            text-decoration: none;
            transition: background 0.2s ease;
        }

        .event:hover {
            background: rgba(255, 255, 255, 0.05);
        }

        .event:focus-visible {
            outline: 3px solid var(--h-sun);
            outline-offset: -3px;
        }

        .date {
            display: flex;
            flex-direction: column;
            line-height: 1.1;
        }

        .weekday, .month {
            font-size: 0.95rem;
            color: rgba(255, 255, 255, 0.6);
        }

        .day {
            white-space: nowrap;
            font-family: var(--h-display);
            font-size: 1.6rem;
            font-weight: 800;
            letter-spacing: -0.02em;
        }

        /* The event's own picture, square, or a quiet calendar mark when it has none. */
        .thumb {
            display: grid;
            place-items: center;
            width: 64px;
            height: 64px;
            border-radius: 14px;
            overflow: hidden;
            background: rgba(255, 255, 255, 0.08);
            color: rgba(255, 255, 255, 0.45);
        }

        .thumb img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .what {
            display: flex;
            flex-direction: column;
            gap: 4px;
            min-width: 0;
        }

        .title {
            font-family: var(--h-display);
            font-size: 1.2rem;
            font-weight: 700;
            line-height: 1.3;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .where {
            font-size: 0.92rem;
            color: rgba(255, 255, 255, 0.62);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .meta {
            display: flex;
            flex-direction: column;
            gap: 4px;
            font-size: 0.92rem;
        }

        .when {
            color: white;
        }

        .counts {
            color: var(--h-mint);
            font-weight: 600;
        }

        .go {
            color: rgba(255, 255, 255, 0.5);
            transition: transform 0.2s ease, color 0.2s ease;
        }

        .event:hover .go {
            color: var(--h-mint);
            transform: translateX(4px);
        }

        @media (max-width: 760px) {
            /* Every row as tall as the fullest one can get (a two-line title, the place, the time
               and the counts on a line each), its content centred: the browser's refetch swaps the
               baked list for fresh events, and rows that sized to their text would move the page. */
            .event {
                box-sizing: border-box;
                grid-template-columns: 70px minmax(0, 1fr) 24px;
                align-content: center;
                gap: 6px 14px;
                min-height: calc(2 * 1.3 * 1.2rem + 1.4 * 0.92rem + 2 * 1.4 * 0.85rem + 4px + 6px + 2px + 36px);
                padding: 18px 12px;
            }

            .where {
                line-height: 1.4;
            }

            .thumb {
                display: none;
            }

            .date {
                grid-row: 1 / span 2;
            }

            .day {
                font-size: 1.3rem;
            }

            .title {
                white-space: normal;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
            }

            .meta {
                grid-column: 2;
                grid-row: 2;
                gap: 2px;
                font-size: 0.85rem;
                line-height: 1.4;
                min-width: 0;
            }

            .when, .counts {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .go {
                grid-column: 3;
                grid-row: 1 / span 2;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .event, .go { transition: none; }
        }
    `],
})
export class EventListComponent {

    readonly events = input.required<FeaturedEvent[]>();

    private readonly locale = inject(LOCALE_ID);

    /** Events whose picture failed to load fall back to the calendar mark instead of a broken box. */
    readonly brokenImages = signal<ReadonlySet<string>>(new Set());

    readonly rows = computed<EventRow[]>(() => this.events()
        // A page baked days ago can still carry events that have ended; they go before paint.
        .filter(({event}) => !event.canceled && event.startsAt &&
            new Date(event.endsAt ?? event.startsAt).getTime() > Date.now())
        // A row without the event's picture reads as unfinished next to the ones that have it.
        .filter(({event}) => event.imageUrl || event.imageFullUrl)
        // At most the rows the server was asked for (an older baked page may carry a few more).
        .slice(0, EVENT_ROWS)
        // A list under date blocks reads as a calendar, so it runs in date order.
        .sort((a, b) => new Date(a.event.startsAt!).getTime() - new Date(b.event.startsAt!).getTime())
        .map(({event: e, activityCount}) => {
            const start = new Date(e.startsAt!);
            // Explicit even when missing: the server renders in UTC, and a zone left to each side
            // would print a different day before and after hydration.
            const timeZone = e.timeZone ?? 'UTC';
            const end = e.endsAt ? new Date(e.endsAt) : null;
            const dayKey = new Intl.DateTimeFormat('en-CA', {timeZone, year: 'numeric', month: '2-digit', day: '2-digit'});
            const time = new Intl.DateTimeFormat(this.locale, {hour: 'numeric', minute: '2-digit', timeZone});
            // The date block already says which day it starts. A weekend convention adds the span of
            // days it runs ("Sat – Sun"), a longer one its dates (a week would read "Sat – Sat"), a
            // game night its hours — also one that runs past midnight: an end before 6am belongs to the
            // night before, so 19:40 – 01:40 does not read as "Fri – Sat".
            const nightEnd = end && new Date(Math.max(start.getTime(), end.getTime() - 6 * 3_600_000));
            const multiDay = nightEnd && dayKey.format(start) !== dayKey.format(nightEnd);
            const withinAWeek = end && end.getTime() - start.getTime() < 6 * 86_400_000;
            const range = new Intl.DateTimeFormat(this.locale, withinAWeek
                ? {weekday: 'short', timeZone}
                : {day: 'numeric', month: 'short', timeZone}) as
                Intl.DateTimeFormat & {formatRange(a: Date, b: Date): string};
            const when = end && multiDay
                ? range.formatRange(start, end)
                : end ? `${time.format(start)} – ${time.format(end)}` : time.format(start);
            return {
                id: e.id,
                url: appEventUrl(e.id),
                title: e.title,
                weekday: new Intl.DateTimeFormat(this.locale, {weekday: 'short', timeZone}).format(start),
                ...dayAndMonth(start, this.locale, timeZone),
                where: [e.organizer.name || e.organizer.username, e.location?.city].filter(Boolean).join(' · '),
                when,
                going: e.goingCount,
                // The preview, not the original: the box is 64px.
                image: e.imageUrl ?? e.imageFullUrl ?? null,
                activities: activityCount,
            };
        }));

    markBroken(id: string): void {
        this.brokenImages.update(broken => new Set(broken).add(id));
    }

    onOpen(id: string): void {
        trackAppClick('landing-events', `/events/${id}`);
    }
}


/**
 * The day of the month and the month, apart. Formatted together they read "Nov 28" in English but
 * "28 de nov." in Catalan, which is wider than the date column and ran into the event's title. Each
 * is formatted on its own so the month comes in its standalone form ("nov.", not "de nov.").
 */
function dayAndMonth(date: Date, locale: string, timeZone: string): {day: string; month: string} {
    return {
        day: new Intl.DateTimeFormat(locale, {day: 'numeric', timeZone}).format(date),
        month: new Intl.DateTimeFormat(locale, {month: 'short', timeZone}).format(date),
    };
}
